import { uniqBy } from 'lodash';

import { AFFILIATE, PHYSICIAN_TEAM, SKILLED_NURSING_FACILITY } from 'constants/locationTypes';
import Episode from 'models/Episode';
import { GroupTypeOptions } from 'models/GroupType';
import LocationEpisode from 'models/LocationEpisode';
import User, { getDefaults as getUserModelDefaults, UserOptions } from 'models/User';
import DischargeCalendarFilter from 'models/userPreferences/DischargeCalendarFilter';
import InsightsFilter from 'models/userPreferences/InsightsFilter';
import PortfolioFilter from 'models/userPreferences/PortfolioFilter';

import LocationEpisodeSummary from './LocationEpisodeSummary';

export const PERMISSIONS = {
  adminAIPromptCreate: 'adminAIPromptCreate',
  adminAIPromptDelete: 'adminAIPromptDelete',
  adminAIPromptEdit: 'adminAIPromptEdit',
  adminAIPromptPublish: 'adminAIPromptPublish',
  adminAIPromptView: 'adminAIPromptView',
  adminAICriteriaCreate: 'adminAICriteriaCreate',
  adminAICriteriaDelete: 'adminAICriteriaDelete',
  adminAICriteriaEdit: 'adminAICriteriaEdit',
  adminAICriteriaView: 'adminAICriteriaView',
  adminAttrView: 'adminAttrView',
  adminAttrCreate: 'adminAttrCreate',
  adminAttrEdit: 'adminAttrEdit',
  adminAttrValueView: 'adminAttrValueView',
  adminAttrValueCreate: 'adminAttrValueCreate',
  adminAttrValueEdit: 'adminAttrValueEdit',
  adminClassificationCreate: 'adminClassificationCreate',
  adminClassificationEdit: 'adminClassificationEdit',
  adminClassificationView: 'adminClassificationView',
  adminClientCreate: 'adminClientCreate',
  adminClientEdit: 'adminClientEdit',
  adminClientView: 'adminClientView',
  adminClientGroupTypeEdit: 'adminClientGroupTypeEdit',
  adminClientGroupTypeView: 'adminClientGroupTypeView',
  adminCollaborationEdit: 'adminCollaborationEdit',
  adminCollaborationView: 'adminCollaborationView',
  adminDischargeReasonCreate: 'adminDischargeReasonCreate',
  adminDischargeReasonEdit: 'adminDischargeReasonEdit',
  adminDischargeReasonView: 'adminDischargeReasonView',
  adminFeatureFlagEdit: 'adminFeatureFlagEdit',
  adminFeatureFlagView: 'adminFeatureFlagView',
  adminFlagEdit: 'adminFlagEdit',
  adminFlagView: 'adminFlagView',
  adminGroupCreate: 'adminGroupCreate',
  adminGroupDelete: 'adminGroupDelete',
  adminGroupEdit: 'adminGroupEdit',
  adminGroupView: 'adminGroupView',
  adminGroupTypeCreate: 'adminGroupTypeCreate',
  adminGroupTypeEdit: 'adminGroupTypeEdit',
  adminGroupTypeView: 'adminGroupTypeView',
  adminImportedPatientEdit: 'adminImportedPatientEdit',
  adminImportedPatientView: 'adminImportedPatientView',
  adminImportConfigurationCreate: 'adminImportConfigurationCreate',
  adminImportConfigurationEdit: 'adminImportConfigurationEdit',
  adminImportConfigurationView: 'adminImportConfigurationView',
  adminImportView: 'adminImportView',
  adminLinkedRecordEdit: 'adminLinkedRecordEdit',
  adminLinkedRecordView: 'adminLinkedRecordView',
  adminLocationCreate: 'adminLocationCreate',
  adminLocationEdit: 'adminLocationEdit',
  adminLocationView: 'adminLocationView',
  adminQhinConfigurationView: 'adminQhinConfigurationView',
  adminQhinConfigurationCreate: 'adminQhinConfigurationCreate',
  adminQhinConfigurationEdit: 'adminQhinConfigurationEdit',
  adminQuestionTemplateCreate: 'adminQuestionTemplateCreate',
  adminQuestionTemplateView: 'adminQuestionTemplateView',
  adminQuestionTemplateEdit: 'adminQuestionTemplateEdit',
  adminUserCreate: 'adminUserCreate',
  adminUserEdit: 'adminUserEdit',
  adminUserInvitationCreate: 'adminUserInvitationCreate',
  adminUserDelete: 'adminUserDelete',
  adminUserView: 'adminUserView',
  aiJobComparisonCreate: 'aiJobComparisonCreate',
  aiJobSummaryCreate: 'aiJobSummaryCreate',
  activityKeyDetailsCreate: 'activityKeyDetailsCreate',
  activityNoteCreate: 'activityNoteCreate',
  activityProgressUpdateCreate: 'activityProgressUpdateCreate',
  clientClassificationsEdit: 'clientClassificationsEdit',
  escalationCreate: 'escalationCreate',
  escalationEdit: 'escalationEdit',
  locationEpisodeDelete: 'locationEpisodeDelete',
  locationEpisodeUserEdit: 'locationEpisodeUserEdit',
  patientCreate: 'patientCreate',
  patientDelete: 'patientDelete',
  patientEdit: 'patientEdit',
  patientView: 'patientView',
  priorityNoteCreate: 'priorityNoteCreate',
  priorityNoteEdit: 'priorityNoteEdit',
  rehabStateAdmissionCreate: 'rehabStateAdmissionCreate',
  rehabStateDischargeCreate: 'rehabStateDischargeCreate',
  rehabStateDischargeEdit: 'rehabStateDischargeEdit',
  rehabStateEdit: 'rehabStateEdit',
  reviewAuthorizationCreate: 'reviewAuthorizationCreate',
  reviewAuthorizationEdit: 'reviewAuthorizationEdit',
  reviewAltcsApplicationEdit: 'reviewAltcsApplicationEdit',
  reviewCoordinateRequestCreate: 'reviewCoordinateRequestCreate',
  reviewCoordinateRequestEdit: 'reviewCoordinateRequestEdit',
  reviewProjectedDischargeCreate: 'reviewProjectedDischargeCreate',
  reviewProjectedDischargeEdit: 'reviewProjectedDischargeEdit',
  reviewReferralCreate: 'reviewReferralCreate',
  reviewReferralEdit: 'reviewReferralEdit',
  reviewSafeReturnCreate: 'reviewSafeReturnCreate',
  reviewSafeReturnEdit: 'reviewSafeReturnEdit',
  reviewAdmissionCreate: 'reviewAdmissionCreate',
  reviewAdmissionDelete: 'reviewAdmissionDelete',
  reviewAdmissionEdit: 'reviewAdmissionEdit',
  reviewAdmissionView: 'reviewAdmissionView',
  reviewServiceRefusalCreate: 'reviewServiceRefusalCreate',
  reviewServiceRefusalEdit: 'reviewServiceRefusalEdit',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const PRODUCT_ACCESSORS = {
  olioSummarizationEnabled: 'olioSummarizationEnabled',
  referralSummarizationEnabled: 'referralSummarizationEnabled',
  careCoordinationEnabled: 'careCoordinationEnabled',
  dischargeCalendarEnabled: 'dischargeCalendarEnabled',
  admissionsManagementEnabled: 'admissionsManagementEnabled',
  inboundReferralsEnabled: 'inboundReferralsEnabled',
} as const;

export type ProductAccessor = (typeof PRODUCT_ACCESSORS)[keyof typeof PRODUCT_ACCESSORS];

export type Accessor = Permission | ProductAccessor;

export interface PatientContext {
  episode?: Episode;
  locationEpisode?: LocationEpisode | LocationEpisodeSummary;
}

export interface SelectedGroup {
  id: string;
  groupType: string | null;
}

export interface ProfileOptions extends UserOptions {
  selectedGroups: SelectedGroup[];
  allowedGroupIds: string[];
  upstreamNetworkClientNames: string[];
  canActAsManager: boolean;
  canActAsProvider: boolean;
  isAdmin: boolean;
  acceptedTermsOfService: boolean;
  enabledProviderTypes: GroupTypeOptions[];
}

function getDefaults(): ProfileOptions {
  return {
    ...getUserModelDefaults(),
    acceptedTermsOfService: false,
    allowedGroupIds: [],
    upstreamNetworkClientNames: [],
    canActAsManager: false,
    canActAsProvider: false,
    isAdmin: false,
    selectedGroups: [],
  };
}

/**
 * @class Profile
 * @extends User
 * @classdesc Represents a user profile in the system
 * @property {SelectedGroup[]} selectedGroups - The profile's selected groups with type info
 * @property {string[]} allowedGroupIds - The profile's allowed location ids
 * @property {boolean} canActAsManager - Whether the user can act as a manager
 * @property {boolean} canActAsProvider - Whether the user can act as a provider
 * @property {boolean} isAdmin - Whether the profile is an admin
 * @property {boolean} acceptedTermsOfService - Whether the profile has accepted the terms of service
 * @param {Partial<ProfileOptions>} [options={}]
 * @example const profile = new Profile({ id: '123' });
 */

export default class Profile extends User {
  acceptedTermsOfService: boolean;
  allowedGroupIds: string[];
  upstreamNetworkClientNames: string[];
  canActAsManager: boolean;
  canActAsProvider: boolean;
  isAdmin: boolean;
  selectedGroups: SelectedGroup[];

  constructor(options?: Partial<ProfileOptions>) {
    const opts = { ...getDefaults(), ...(options ?? {}) };

    super(opts);

    this.selectedGroups = opts.selectedGroups;
    this.canActAsManager = opts.canActAsManager;
    this.canActAsProvider = opts.canActAsProvider;
    this.isAdmin = opts.isAdmin;
    this.acceptedTermsOfService = opts.acceptedTermsOfService;
    this.allowedGroupIds = opts.allowedGroupIds;
    this.upstreamNetworkClientNames = opts.upstreamNetworkClientNames;
    this.groupType = opts.groupType;
  }

  get selectedGroupIds(): string[] {
    return this.selectedGroups.map((g) => g.id);
  }

  get insightsPreference() {
    return this.preferences?.find(
      (pref): pref is InsightsFilter => pref.clientId === this.actingClient?.id && pref instanceof InsightsFilter
    );
  }

  get portfolioPreference() {
    return this.preferences?.find(
      (pref): pref is PortfolioFilter => pref.clientId === this.actingClient?.id && pref instanceof PortfolioFilter
    );
  }

  get dischargeCalendarPreference() {
    return this.preferences?.find(
      (pref): pref is DischargeCalendarFilter =>
        pref.clientId === this.actingClient?.id && pref instanceof DischargeCalendarFilter
    );
  }

  get isSnfCustomerUser() {
    return (
      this.managedProviderTypes.some((gt) => gt.apiName === SKILLED_NURSING_FACILITY) &&
      this.providedProviderTypes.some((gt) => gt.apiName === SKILLED_NURSING_FACILITY)
    );
  }

  get referralSummarizationEnabled() {
    return this.actingClient.referralSummarizationEnabled;
  }

  get olioSummarizationEnabled() {
    return this.actingClient.olioSummarizationEnabled;
  }

  get careCoordinationEnabled() {
    return this.actingClient.careCoordinationEnabled;
  }

  get dischargeCalendarEnabled() {
    return this.actingClient.dischargeCalendarEnabled;
  }

  get admissionsManagementEnabled() {
    return this.actingClient.admissionsManagementEnabled;
  }

  get inboundReferralsEnabled() {
    return this.actingClient.inboundReferralsEnabled;
  }

  get isPhysicianTeamUser() {
    return this.groupType === PHYSICIAN_TEAM;
  }

  get clientType() {
    return this.actingClient.clientType;
  }

  get canCreatePatient() {
    if (this.isAffiliateUser) return false;

    return this.has(PERMISSIONS.patientCreate);
  }

  isManagerFor(episode?: Episode) {
    if (!episode) throw new Error('An episode is required to check if user is a manager for this patient context');

    const isManagerForEpisode = episode && this.canAccessGroup(episode.owner.id);
    const isAffiliateForEpisode =
      this.isAffiliateUser && episode?.affiliates.map((a) => a.clientId).includes(this.actingClient.id);

    return isManagerForEpisode || isAffiliateForEpisode;
  }

  isProviderFor(groupId?: string) {
    if (!groupId)
      throw new Error(
        "A location episode's group id is required to check if user is a provider for this patient context"
      );

    return this.canAccessGroup(groupId);
  }

  isProviderForClientManagedAndProvidedGroupType(groupTypeId: string): boolean {
    return this.actingClient.managesAndProvidesGroupType(groupTypeId) && this.canActAsProvider;
  }

  // Whether the user can create a progress update on the primary LE or any of the provided
  // linked records. Linked records are included to support flows where the user manages
  // the primary (e.g., SNF CC building user on their own SNF CC patient) but can still
  // create a progress update on an upstream shared record.
  canCreateProgressUpdate(episode: Episode, locationEpisode: LocationEpisode, linkedRecords: LocationEpisode[] = []) {
    if (this.has(PERMISSIONS.activityProgressUpdateCreate, { episode, locationEpisode })) return true;

    return linkedRecords.some((record) =>
      this.has(PERMISSIONS.activityProgressUpdateCreate, {
        episode: new Episode({ id: record.episodeId, owner: record.owner }),
        locationEpisode: record,
      })
    );
  }

  canAccessGroup(groupId: string) {
    return this.allowedGroupIds.includes(groupId);
  }

  canCreateUmAiSummaryFor({ episode, locationEpisode }: PatientContext = {}) {
    return (
      ((locationEpisode as LocationEpisode)?.umAISummaryEnabled &&
        this.hasAny([PERMISSIONS.reviewAuthorizationCreate, PERMISSIONS.reviewAuthorizationEdit], {
          episode,
          locationEpisode,
        })) ??
      false
    );
  }

  get isAffiliateUser() {
    return this.clientType?.toLowerCase() === AFFILIATE;
  }

  get isPowerUser() {
    return this.canActAsManager && this.canActAsProvider;
  }

  has(accessor: Accessor, patientContext: PatientContext = {}) {
    return this.hasAccessor(accessor) && this.isAllowedForPatientContext(accessor, patientContext);
  }

  hasAll(accessors: Accessor[], patientContext: PatientContext = {}) {
    return accessors.every((accessor) => this.has(accessor, patientContext));
  }

  hasAny(accessors: Accessor[], patientContext: PatientContext = {}) {
    return accessors.some((accessor) => this.has(accessor, patientContext));
  }

  hasFlag(flag: string) {
    return this.flags.includes(flag);
  }

  get canChangeClientScope() {
    return !!this.isAdmin || (this.client?.leafDescendants.length || 0) > 1;
  }

  get tabProviderTypes() {
    const types = [
      ...(this.canActAsManager ? this.managedProviderTypes : []),
      ...(this.canActAsProvider ? this.providedProviderTypes : []),
    ].sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''));

    return uniqBy(types, 'id');
  }

  private hasAccessor(accessor: Accessor): boolean {
    const isPermission = Object.values(PERMISSIONS).includes(accessor as Permission);

    if (!this.permissions && isPermission) return false;

    return this[accessor] || this.permissions?.[accessor] || false;
  }

  private isAllowedForPatientContext = (accessor: Accessor, { episode, locationEpisode }: PatientContext) => {
    const groupId =
      locationEpisode && 'groupId' in locationEpisode ? locationEpisode?.groupId : locationEpisode?.group?.id;

    // Any accessor not explicitly listed here will be allowed if the profile has been granted that accessor
    switch (accessor) {
      case PERMISSIONS.escalationEdit:
      case PERMISSIONS.locationEpisodeDelete:
      case PERMISSIONS.locationEpisodeUserEdit:
      case PERMISSIONS.patientDelete:
      case PERMISSIONS.rehabStateEdit:
      case PERMISSIONS.reviewAuthorizationCreate:
      case PERMISSIONS.reviewAuthorizationEdit:
      case PERMISSIONS.reviewProjectedDischargeCreate:
      case PERMISSIONS.reviewProjectedDischargeEdit:
        return this.isManagerFor(episode); // manager for the patient context
      case PERMISSIONS.activityProgressUpdateCreate:
      case PERMISSIONS.escalationCreate:
      case PERMISSIONS.priorityNoteEdit:
      case PERMISSIONS.reviewServiceRefusalCreate:
      case PERMISSIONS.reviewSafeReturnCreate:
        return !this.isManagerFor(episode) && this.isProviderFor(groupId); // provider for the patient context
      case PERMISSIONS.rehabStateAdmissionCreate:
      case PERMISSIONS.rehabStateDischargeCreate:
      case PERMISSIONS.reviewReferralCreate:
      case PERMISSIONS.reviewSafeReturnEdit:
        return this.isManagerFor(episode) || this.isProviderFor(groupId); // manager or provider for the patient context
      default:
        return true;
    }
  };
}
